// src/components/ai-prayer/companion/lib/abielApi.ts
import { callFunction } from '@/integrations/supabase/functions';
import { detectRiskFlag, type RiskFlag } from '@/utils/risk';

export type ChatMessage =
  | { role: 'system' | 'user' | 'assistant'; content: string }
  | { role: 'tool'; content: string; name?: string };

export interface AbielChatOptions {
  topic?: 'guide' | 'prayer' | 'bible' | 'study';
  systemPrompt?: string;
  provider?: 'openai' | 'perplexity' | 'hybrid';
}

export interface AbielGuidance {
  mode: 'guide';
  summary: string;
  reading_goals: string[];
  how_to_pray: {
    outline: {
      address: string;
      praise: string;
      confession?: string;
      request: string;
      submission: string;
      thanksgiving: string;
    };
    bullet_points: string[];
  };
  prompts: {
    en: string[];
    zh?: string[];
  };
  journaling_questions: string[];
  cross_references: string[];
  notes: string;
  source?: 'OpenAI' | 'Perplexity' | 'Hybrid';
}

export interface AbielGuidanceResult {
  ok: boolean;
  mode: 'guide';
  guidance: string;
  message?: string;
  riskFlag: RiskFlag | null;
  usedESVVerse?: boolean;
  cacheStatus?: 'hit' | 'miss' | 'n/a';
  usedFunction?: string;
}

/**
 * Abiel Chat — calls EF and normalizes guidance + risk flag
 */
export async function abielChat(
  messages: ChatMessage[],
  opts: AbielChatOptions = {}
): Promise<AbielGuidanceResult> {
  const { data, error, status } = await callFunction('abiel-prayer', {
    body: {
      messages,
      topic: opts.topic ?? 'guide',
      systemPrompt: opts.systemPrompt,
      provider: opts.provider ?? 'openai',
    },
  });

  if (error || status >= 400) {
    throw new Error(error?.message || `abiel-prayer failed (${status})`);
  }

  // normalize guidance content
  let content = '';
  if (data?.guidance) {
    if (typeof data.guidance === 'string') {
      const trimmed = data.guidance.trim();
      content = trimmed === '{}' || trimmed === '' ? '' : trimmed;
    } else if (typeof data.guidance === 'object') {
      const keys = Object.keys(data.guidance || {});
      content = keys.length > 0 ? JSON.stringify(data.guidance) : '';
    }
  } else if (data?.message) {
    content = data.message;
  } else if (data?.prayer) {
    content = data.prayer;
  }

  // normalize risk flag (server-provided or client fallback)
  let riskFlag: RiskFlag | null = null;
  if (data?.riskFlag) {
    riskFlag =
      typeof data.riskFlag === 'string'
        ? detectRiskFlag(data.riskFlag)
        : data.riskFlag;
  }
  if (!riskFlag) {
    riskFlag = detectRiskFlag(content || '');
  }

  return {
    ok: true,
    mode: data?.mode ?? 'guide',
    guidance: content,
    message: data?.message ?? '',
    riskFlag,
    usedESVVerse: data?.usedESVVerse ?? false,
    cacheStatus: data?.cacheStatus ?? 'n/a',
    usedFunction: data?.usedFunction,
  };
}

/**
 * Bible passage fetcher (server EF hides API keys)
 */
export async function fetchPassage(reference: string) {
  const { data, error, status } = await callFunction('bible-search', {
    body: { passage: reference, mode: 'verses', version: 'ESV' },
  });
  if (error || status >= 400) {
    throw new Error(error?.message || `bible-search failed (${status})`);
  }
  return data?.data?.verses || [];
}

/**
 * Bible passage searcher
 */
export async function searchPassages(query: string, limit = 5) {
  const { data, error, status } = await callFunction('bible-search', {
    body: { action: 'search', query, limit },
  });
  if (error || status >= 400) {
    throw new Error(error?.message || `bible-search search failed (${status})`);
  }
  return data?.verses || [];
}

/**
 * Abiel Guidance — direct structured guidance mode
 */
export async function abielGuidance(params: {
  requestText: string;
  subject?: string;
  selectedTags?: string[];
  bilingual?: boolean;
  allowedReferences?: string[];
  provider?: 'openai' | 'perplexity' | 'hybrid';
}): Promise<AbielGuidanceResult> {
  const { data, error, status } = await callFunction('abiel-prayer', {
    body: {
      mode: 'guide',
      requestText: params.requestText,
      subject: params.subject,
      selectedTags: params.selectedTags,
      bilingual: params.bilingual,
      allowedReferences: params.allowedReferences,
      provider: params.provider ?? 'openai',
    },
  });

  if (error || status >= 400) {
    throw new Error(error?.message || `abiel-guidance failed (${status})`);
  }

  const raw = data?.guidance || '';
  const guidance = typeof raw === 'object' ? JSON.stringify(raw) : raw;

  // normalize risk flag
  let riskFlag: RiskFlag | null = null;
  if (data?.riskFlag) {
    riskFlag =
      typeof data.riskFlag === 'string'
        ? detectRiskFlag(data.riskFlag)
        : data.riskFlag;
  }
  if (!riskFlag) {
    riskFlag = detectRiskFlag(guidance || '');
  }

  return {
    ok: true,
    mode: 'guide',
    guidance,
    message: data?.message ?? '',
    riskFlag,
    usedESVVerse: data?.usedESVVerse ?? false,
    cacheStatus: data?.cacheStatus ?? 'n/a',
    usedFunction: data?.usedFunction,
  };
}

/**
 * Trends & Felt Needs for a verse (research brief)
 */
export async function abielTrends(
  verse: { reference: string; esvText?: string },
  options?: {
    audience?: 'general' | 'youth' | 'global';
    timeWindowDays?: number;
    provider?: 'openai' | 'perplexity' | 'hybrid'; // defaults to 'perplexity'
    topic?: 'study' | 'bible';                      // defaults to 'study'
  }
) {
  const { buildTrendsPrompt } = await import('@/utils/abiel/abielTrendsPrompt');
  const prompt = buildTrendsPrompt({
    reference: verse.reference,
    esvText: verse.esvText,
    audience: options?.audience ?? 'general',
    timeWindowDays: options?.timeWindowDays ?? 90,
  });

  // Reuse the existing transport
  const system: ChatMessage = {
    role: 'system',
    content:
      'You return a concise research brief with exactly the requested headings, using the specified sources and date formatting. Do not generate prayers.',
  };

  return abielChat(
    [system, { role: 'user', content: prompt }],
    {
      topic: options?.topic ?? 'study',
      provider: options?.provider ?? 'perplexity',
    }
  );
}
